import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { AgreementFormData } from ".";
import CloseIcon from "@mui/icons-material/Close";

interface CustomerAgreementEditModalProps {
  open: boolean;
  values: AgreementFormData;
  handleClose: () => void;
  setValues: React.Dispatch<React.SetStateAction<AgreementFormData>>;
}

export default function CustomerAgreementEditModal({
  open,
  values,
  handleClose,
  setValues,
}: CustomerAgreementEditModalProps) {
  const [formData, setFormData] = useState<AgreementFormData>(values);

  useEffect(() => {
    setFormData(values);
  }, [values]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      sameAsClientAddress: checked,
      outletAddress: checked ? prev.clientAddress : "",
    }));
  };

  const handleClientAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      clientAddress: value,
      outletAddress: prev.sameAsClientAddress ? value : prev.outletAddress,
    }));
  };

  const handleSave = () => {
    setValues(formData);
    handleClose();
  };

  const handleCancel = () => {
    setFormData(values);
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          maxWidth: "460px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "18px 20px 14px",
          borderBottom: "1px solid #e2e0d8",
          marginBottom: "16px",
        }}
      >
        <Typography
          sx={{
            fontSize: "17px",
            fontWeight: 600,
            color: "#111111",
            lineHeight: "22px",
            letterSpacing: "-0.1px",
            fontFamily: `"Inter", "Roboto", "Helvetica", "Arial", sans-serif`,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textTransform: "none",
          }}
        >
          Edit Agreement
        </Typography>
        <IconButton
          onClick={handleCancel}
          sx={{
            color: "#888780",
            padding: "4px",
            "&:hover": {
              color: "#1a1a18",
              backgroundColor: "#f5f5f3",
            },
          }}
        >
          <CloseIcon sx={{ width: 18, height: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#2f2f2d",
              marginBottom: "5px",
            }}
          >
            Client Name
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="clientName"
            value={formData.clientName || ""}
            onChange={handleChange}
            placeholder="Enter client name"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                color: "#1a1a18",
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#d0cec6",
                },
                "&:hover fieldset": {
                  borderColor: "#d0cec6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e05a1e",
                  boxShadow: "0 0 0 3px rgba(224, 90, 30, 0.1)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#b4b2a9",
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#2f2f2d",
              marginBottom: "5px",
            }}
          >
            Client Address
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="clientAddress"
            value={formData.clientAddress || ""}
            onChange={handleClientAddressChange}
            placeholder="Enter client address"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                color: "#1a1a18",
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#d0cec6",
                },
                "&:hover fieldset": {
                  borderColor: "#d0cec6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e05a1e",
                  boxShadow: "0 0 0 3px rgba(224, 90, 30, 0.1)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#b4b2a9",
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#2f2f2d",
              marginBottom: "5px",
            }}
          >
            Client Signatory
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            name="clientSignatory"
            value={formData.clientSignatory || ""}
            onChange={handleChange}
            placeholder="Enter signatory name"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                color: "#1a1a18",
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#d0cec6",
                },
                "&:hover fieldset": {
                  borderColor: "#d0cec6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e05a1e",
                  boxShadow: "0 0 0 3px rgba(224, 90, 30, 0.1)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#b4b2a9",
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#2f2f2d",
              marginBottom: "5px",
            }}
          >
            Outlet Address
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginTop: "2px",
              marginBottom: "5px",
            }}
          >
            <Checkbox
              checked={formData.sameAsClientAddress}
              onChange={handleCheckboxChange}
              size="small"
              sx={{
                padding: 0,
                "& .MuiSvgIcon-root": {
                  width: 16,
                  height: 16,
                },
                "&.Mui-checked": {
                  color: "#e05a1e",
                },
                "& .MuiCheckbox-root": {
                  color: "#d0cec6",
                },
              }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#2f2f2d",
                marginBottom: "5px",
              }}
            >
              Same as Client Address
            </Typography>
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            name="outletAddress"
            value={formData.outletAddress || ""}
            onChange={handleChange}
            placeholder="Enter outlet address"
            size="small"
            disabled={formData.sameAsClientAddress}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "14px",
                color: formData.sameAsClientAddress ? "#888780" : "#1a1a18",
                backgroundColor: formData.sameAsClientAddress
                  ? "#faf9f7"
                  : "#ffffff",
                borderRadius: "6px",
                "& fieldset": {
                  borderColor: "#d0cec6",
                },
                "&:hover fieldset": {
                  borderColor: "#d0cec6",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#e05a1e",
                  boxShadow: "0 0 0 3px rgba(224, 90, 30, 0.1)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#b4b2a9",
              },
              "& .Mui-disabled": {
                backgroundColor: "#faf9f7",
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
          padding: "14px 20px 20px",
          borderTop: "1px solid #e2e0d8",
        }}
      >
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            backgroundColor: "#e05a1e",
            color: "#ffffff",
            borderRadius: "6px",
            padding: "11px 0",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#c04a14",
            },
          }}
        >
          Confirm
        </Button>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#e05a1e",
            backgroundColor: "#ffffff",
            borderColor: "#e05a1e",
            borderWidth: "1.5px",
            borderRadius: "6px",
            padding: "11px 0",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#fff5f0",
              borderColor: "#e05a1e",
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
